import { forwardRef, useImperativeHandle } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  FileUpload,
  Modal,
  ModalControlModalProps,
  Text,
  useFileUpload,
  type AcceptedFileType,
} from '@frontend/design-system';
import { MAX_FILE_SIZE } from '../../constants';

const IMAGE_FILE_TYPES: AcceptedFileType = ['jpeg', 'jpg', 'png'];
const MULTI_FILE_TYPES: AcceptedFileType = ['jpeg', 'jpg', 'png', 'docx', 'pdf'];

interface Props {
  modalProps: ModalControlModalProps;
  onImageUpload: (files: File[]) => Promise<void>;
  isPDFandDocxAllowed?: boolean;
}

export interface ImageUploadModalHandle {
  // Lets callers open the native file picker directly, bypassing the modal UI.
  openFilePicker: () => void;
  resetFiles: () => void;
}

export const ImageUploadModal = forwardRef<ImageUploadModalHandle, Props>(
  ({ modalProps, onImageUpload, isPDFandDocxAllowed }, ref) => {
    const { t } = useTranslation('team-chat');

    const helperLabel = isPDFandDocxAllowed
      ? t('Accepted file formats: .jpeg, .jpg, .png, .docx and .pdf')
      : t('Accepted file formats: .jpeg, .jpg and .png');
    const acceptedFormats = isPDFandDocxAllowed ? MULTI_FILE_TYPES : IMAGE_FILE_TYPES;

    const handleFileUpload = async (files: File[]) => {
      await onImageUpload(files);
      modalProps.onClose();
    };

    // Shares accepted types/size with the modal's FileUpload so the hidden input can drive the same upload flow.
    const {
      handleOnClick: openFilePicker,
      inputProps: nativeInputProps,
      resetFiles,
    } = useFileUpload({
      acceptedFileType: acceptedFormats,
      maxFileSize: MAX_FILE_SIZE,
      onFileUpload: handleFileUpload,
    });

    useImperativeHandle(
      ref,
      () => ({
        openFilePicker,
        resetFiles,
      }),
      [openFilePicker, resetFiles]
    );

    return isPDFandDocxAllowed ? (
      <input type='file' hidden multiple {...nativeInputProps} />
    ) : (
      <Modal {...modalProps} maxWidth={600}>
        <Modal.Header
          css={styles.header}
          onClose={modalProps.onClose}
          textAlign='left'
          closeBtnTrackingId='team-chat-2.0-file-upload-modal-close-button'
        >
          {t('Upload Image')}
        </Modal.Header>
        <Modal.Body>
          <FileUpload
            acceptedFileType={acceptedFormats}
            helperText={
              <div css={styles.helperText}>
                <Text>{t('Drop image here')}</Text>
                <Text>{helperLabel}</Text>
                <Text>{t('Max file size: 10MB')}</Text>
                <Text>{t('Max files allowed: 10')}</Text>
              </div>
            }
            trackingId='team-chat-2.0-image-upload-modal'
            multiple
            onFileUpload={handleFileUpload}
          />
        </Modal.Body>
      </Modal>
    );
  }
);

ImageUploadModal.displayName = 'ImageUploadModal';

const styles = {
  header: css`
    margin-bottom: ${theme.spacing(1)};
  `,

  helperText: css`
    margin: ${theme.spacing(0, 3)};
    text-align: center;
  `,
};
